import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { HomeEmptyState } from '@/features/home/components/HomeEmptyState'
import { GreetingRow } from '@/features/home/components/GreetingRow'
import { UrgencyBanner } from '@/features/home/components/UrgencyBanner'
import { FixtureWidget } from '@/features/home/components/FixtureWidget'
import { primaryGame, survivorCompetitionTabs } from '@/features/home/lib/survivorHome'
import type { CompetitionTab } from '@/features/home/lib/survivorHome'
import { SurvivorRoundCard } from '@/features/home/components/SurvivorRoundCard'
import { MyPerformanceCard } from '@/features/home/components/MyPerformanceCard'
import { SubscriptionCard } from '@/features/home/components/SubscriptionCard'
import { ActionCenterCard } from '@/features/home/components/ActionCenterCard'
import { SuggestedPickCard } from '@/features/home/components/SuggestedPickCard'
import { HighlightsCard } from '@/features/home/components/HighlightsCard'
import { fetchMyProfile, profileKeys, useMyProfile } from '@/features/home/queries'
import { fetchUserLeagues, leagueKeys } from '@/features/league/queries'
import { fetchUserSurvivorGames, survivorKeys } from '@/features/sobrevivente/queries'
import { SurvivorGameStatus } from '@/features/sobrevivente/types'
import type { SurvivorGameListItem } from '@/features/sobrevivente/types'
import { useUrgentDeadline } from '@/features/home/hooks/useUrgentDeadline'
import type { LeagueSummary } from '@/features/league/types'
import { LegacySkin } from '@/shared/components/layout/LegacySkin'
import { useTranslation } from '@/shared/i18n/useTranslation'
import styles from './home.module.css'

export const Route = createFileRoute('/_authenticated/home')({
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData({ queryKey: profileKeys.me(), queryFn: fetchMyProfile }),
      queryClient.ensureQueryData({ queryKey: leagueKeys.list(), queryFn: fetchUserLeagues }),
      queryClient.ensureQueryData({
        queryKey: survivorKeys.list(),
        queryFn:  fetchUserSurvivorGames,
      }),
    ]),
  component: HomePage,
})

// ── Derived competition tabs ──────────────────────────────────────────────────

function uniqueCompetitionTabs(leagues: LeagueSummary[]): CompetitionTab[] {
  const seen = new Set<string>()
  const tabs: CompetitionTab[] = []
  for (const league of leagues) {
    if (seen.has(league.competitionId)) continue
    seen.add(league.competitionId)
    tabs.push({
      id:    league.competitionId,
      name:  league.competitionName,
      sport: league.competitionSport,
    })
  }
  return tabs
}

// ── Survivor home ─────────────────────────────────────────────────────────────

function SurvivorHome({ games }: { games: SurvivorGameListItem[] }) {
  const { t } = useTranslation()
  const { data: profile } = useMyProfile()

  const competitions = useMemo(() => survivorCompetitionTabs(games), [games])
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(
    () => competitions[0]?.id ?? '',
  )

  // A competition can disappear (last game left/deleted) while its id is still selected.
  const activeCompetitionId = competitions.some(c => c.id === selectedCompetitionId)
    ? selectedCompetitionId
    : competitions[0]?.id ?? ''

  const gamesInCompetition = useMemo(
    () => games.filter(g => g.competitionId === activeCompetitionId),
    [games, activeCompetitionId],
  )

  const lead = primaryGame(gamesInCompetition)
  const otherGameCount = Math.max(0, gamesInCompetition.length - 1)
  const aliveGames = games.filter(g => g.isAlive && g.status !== SurvivorGameStatus.Finished).length

  if (!profile) return null

  return (
    <div className={styles.pageBody}>
      <GreetingRow
        displayName={profile.displayName}
        subtitle={
          `${t('home.gamesCount', { count: games.length })} · ` +
          `${t('home.activeCount', { count: aliveGames })}`
        }
      />

      <div className={styles.gridMain}>
        {lead && <SurvivorRoundCard game={lead} />}

        {otherGameCount > 0 && (
          <Link to="/sobrevivente" className={styles.moreGames}>
            {t('home.moreGames', { count: otherGameCount })}
          </Link>
        )}

        {competitions.length > 0 && (
          <FixtureWidget
            competitions={competitions}
            selectedCompetitionId={activeCompetitionId}
            onSelectCompetition={setSelectedCompetitionId}
            {...(lead?.currentRound?.myPickTeamId !== undefined
              ? { pickedTeamId: lead.currentRound.myPickTeamId }
              : {})}
          />
        )}
      </div>
    </div>
  )
}

// ── Fantasy home (frozen — only reached by users who still have fantasy leagues) ──

function FantasyHome({ leagues }: { leagues: LeagueSummary[] }) {
  const { data: profile } = useMyProfile()

  const competitions = useMemo(() => uniqueCompetitionTabs(leagues), [leagues])
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(
    () => competitions[0]?.id ?? '',
  )

  const urgentDeadline = useUrgentDeadline(leagues)

  const activeCompetitions = useMemo(
    () => new Set(
      leagues
        .filter(l => l.competitionStatus === 'IN_PROGRESS')
        .map(l => l.competitionId)
    ).size,
    [leagues],
  )

  if (!profile) return null

  return (
    <div className={styles.pageBody}>
      <GreetingRow
        displayName={profile.displayName}
        subtitle={
          `${activeCompetitions} competiç${activeCompetitions === 1 ? 'ão ativa' : 'ões ativas'} · ` +
          `${leagues.length} ${leagues.length === 1 ? 'liga' : 'ligas'}`
        }
      />

      {/* Frozen fantasy CSS is baked for the dark Voltage ground — pin it. */}
      <LegacySkin />

      {urgentDeadline && <UrgencyBanner deadline={urgentDeadline} />}

      <ActionCenterCard leagues={leagues} />

      <div className={styles.grid}>
        <div className={styles.gridMain}>
          {competitions.length > 0 && (
            <FixtureWidget
              competitions={competitions}
              selectedCompetitionId={selectedCompetitionId}
              onSelectCompetition={setSelectedCompetitionId}
            />
          )}
          <SuggestedPickCard leagues={leagues} />
        </div>

        <div className={styles.gridAside}>
          <MyPerformanceCard leagues={leagues} />
          <HighlightsCard leagues={leagues} />
          <SubscriptionCard
            tier={profile.subscriptionTier}
            leagueCount={leagues.length}
          />
        </div>
      </div>
    </div>
  )
}

// ── HomePage ──────────────────────────────────────────────────────────────────

function HomePage() {
  const { data: leagues = [] } = useQuery({
    queryKey: leagueKeys.list(),
    queryFn:  fetchUserLeagues,
  })

  const { data: survivorGames = [] } = useQuery({
    queryKey: survivorKeys.list(),
    queryFn:  fetchUserSurvivorGames,
  })

  // Sobrevivente is the product; fantasy is frozen and off the menus entirely.
  // A player in both sees the survivor home; the fantasy home is only reached by
  // someone who has leagues and no survivor game.
  const content =
    survivorGames.length > 0 ? <SurvivorHome games={survivorGames} />
    : leagues.length > 0     ? <FantasyHome leagues={leagues} />
                             : <HomeEmptyState />

  return (
    <>
      <AppHeader title="Início" />
      {content}
    </>
  )
}
